import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ALL_PROJECTS_VALUE } from '@/lib/projectSelection'
import { cn } from '@/lib/utils'
import type { Project } from '@/types/nf'

const RECENT_PROJECTS_KEY = 'nf.recentProjects'
const RECENT_PROJECTS_LIMIT = 5
export { ALL_PROJECTS_VALUE }

interface ProjectComboboxProps {
  projects: Project[]
  value: string | null | undefined
  onChange: (projectId: string) => void
  isLoading?: boolean
  className?: string
  ariaLabel?: string
  includeAllProjects?: boolean
}

function readRecentProjectIds() {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_PROJECTS_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

function writeRecentProjectIds(projectId: string, previous: string[]) {
  const next = [projectId, ...previous.filter((item) => item !== projectId)].slice(0, RECENT_PROJECTS_LIMIT)
  try {
    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(next))
  } catch {
    // Recent projects are an enhancement; selection still works without storage.
  }
  return next
}

export function ProjectCombobox({ projects, value, onChange, isLoading = false, className, ariaLabel, includeAllProjects = false }: ProjectComboboxProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [recentProjectIds, setRecentProjectIds] = useState<string[]>([])

  useEffect(() => {
    setRecentProjectIds(readRecentProjectIds())
  }, [])

  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects])
  const selectedProject = value ? projectById.get(value) : undefined
  const isAllProjectsSelected = value === ALL_PROJECTS_VALUE
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const filteredProjects = useMemo(
    () =>
      normalizedQuery
        ? projects.filter((project) => project.title.toLocaleLowerCase().includes(normalizedQuery))
        : projects,
    [normalizedQuery, projects],
  )
  const recentProjects = recentProjectIds
    .map((projectId) => projectById.get(projectId))
    .filter((project): project is Project => Boolean(project))
    .filter((project) => !normalizedQuery || project.title.toLocaleLowerCase().includes(normalizedQuery))
  const recentProjectIdSet = new Set(recentProjects.map((project) => project.id))
  const otherProjects = filteredProjects.filter((project) => !recentProjectIdSet.has(project.id))

  const chooseProject = (projectId: string) => {
    if (projectId !== ALL_PROJECTS_VALUE) {
      setRecentProjectIds((previous) => writeRecentProjectIds(projectId, previous))
    }
    onChange(projectId)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? t('project.selectProject')}
          disabled={isLoading || (!projects.length && !includeAllProjects)}
          className={cn('w-[260px] justify-between', className)}
        >
          <span className="truncate">
            {isAllProjectsSelected ? t('project.allProjectsAggregate') : selectedProject?.title ?? (isLoading ? t('common.loading') : t('project.selectProject'))}
          </span>
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('project.searchProject')}
          />
          <CommandList>
            <CommandEmpty>{t('project.noProjectResults')}</CommandEmpty>
            {includeAllProjects && !normalizedQuery ? (
              <CommandGroup>
                <CommandItem value={t('project.allProjectsAggregate')} onSelect={() => chooseProject(ALL_PROJECTS_VALUE)}>
                  <Check className={cn('size-4', value === ALL_PROJECTS_VALUE ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{t('project.allProjectsAggregate')}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {includeAllProjects && !normalizedQuery && (recentProjects.length || otherProjects.length) ? <CommandSeparator /> : null}
            {recentProjects.length ? (
              <CommandGroup heading={t('project.recentProjects')}>
                {recentProjects.map((project) => (
                  <CommandItem key={project.id} value={project.title} onSelect={() => chooseProject(project.id)}>
                    <Check className={cn('size-4', project.id === value ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{project.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {recentProjects.length && otherProjects.length ? <CommandSeparator /> : null}
            {otherProjects.length ? (
              <CommandGroup heading={t('project.allProjects')}>
                {otherProjects.map((project) => (
                  <CommandItem key={project.id} value={project.title} onSelect={() => chooseProject(project.id)}>
                    <Check className={cn('size-4', project.id === value ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{project.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
